import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Stack, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Tooltip, Divider,
  InputAdornment, alpha, useTheme, Select, MenuItem,
  Fade, Zoom, Grid, Badge
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Search, FavoriteBorder, Favorite,
  InfoOutlined, Home, CheckCircle, HourglassEmpty,
  KeyboardArrowRight, Refresh, Close, DeleteOutline,
  HelpOutline, LockOpen, Add, Save, ContentCopy,
  Favorite as FavoriteIcon, ExpandMore, ExpandLess
} from '@mui/icons-material';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek, getISOWeekYear, getWeek } from 'date-fns';

import SearchDropdown from './SearchDropdown';
import {
  getWeekDetails, shiftWeek, getDateOfDay,
  DAY_INITIALS, getTodayIndex, decodeToken, exportTimesheetToExcel
} from './clockwiseUtils';
import {
  getClockwiseTasks, getWeekStatus, getDailyStatus,
  createTasks, updateTask, deleteTask,
  handInWeek, reopenWeek, approveWeek, closeEmployeeWeek,
  closeMultipleDays, getFavorites, addFavorite, removeFavorite,
  getLocalProjectsData,
  closeDay, handInWeekAllUsers, closeDayAllUsers,
} from '../../services/clockwiseAPI';
import { getAllEmployees } from '../../services/HRstaff';

// import Joyride, { STATUS } from 'react-joyride';
const STATUS = { FINISHED: 'finished', SKIPPED: 'skipped' }; // Fallback for logic

// ─── Visual Constants matching Clockwise.js ──────────────────────────────────
const GRID_COLUMNS = '200px repeat(7, 1fr) 80px';
const BORDER_CLR = '#e5e7eb';
const TODAY_BG = '#fff7ed';
const ADMIN_ROLES = ['HR', 'MANAGER', 'DIRECTOR', 'DEV']; // Adjusted for AXO People roles

// Enhanced Custom Tooltip for Joyride
const CustomTooltip = ({
  index, step, tooltipProps, backProps, primaryProps, skipProps, isLastStep, size,
}) => {
  const getStepIcon = (idx) => {
    switch (idx) {
      case 0: return '👋';
      case 1: return '📅';
      case 2: return '🔍';
      case 3: return '📁';
      case 4: return '⏱️';
      case 5: return '✅';
      default: return '✨';
    }
  };

  return (
    <Box
      {...tooltipProps}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        boxShadow: '0 20px 50px -12px rgba(0, 0, 0, 0.25)',
        width: 420, maxWidth: '90vw',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        animation: 'tooltipSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        ...tooltipProps.style,
      }}
    >
      <style>{`
        @keyframes tooltipSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Progress Bar */}
      <Box sx={{ height: 4, bgcolor: '#f3f4f6', width: '100%' }}>
        <Box
          sx={{
            height: '100%',
            background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
            width: `${((index + 1) / size) * 100}%`,
            transition: 'width 0.5s ease-in-out'
          }}
        />
      </Box>

      <Box sx={{ p: 3, pb: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          fontSize: 24, bgcolor: '#eff6ff', width: 48, height: 48,
          borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid #dbeafe'
        }}>
          {getStepIcon(index)}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Step {index + 1} of {size}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' }}>
            {step.title}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#4b5563', mb: 4 }}>
          {step.content}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button {...skipProps} sx={{ color: '#9ca3af', textTransform: 'none', fontWeight: 500 }}>
            Skip
          </Button>
          <Stack direction="row" spacing={1.5}>
            {index > 0 && (
              <Button {...backProps} variant="outlined" sx={{ borderRadius: 2.5, px: 2.5, py: 1, textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#e5e7eb' }}>
                Back
              </Button>
            )}
            <Button {...primaryProps} variant="contained" sx={{
              borderRadius: 2.5, px: 3, py: 1, textTransform: 'none', fontWeight: 600,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
            }}>
              {isLastStep ? 'Finish' : 'Next →'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default function Timesheet() {
  const theme = useTheme();
  const currentUser = useMemo(() => decodeToken(), []);
  const userRole = localStorage.getItem('userRole') || '';
  const isAdmin = ADMIN_ROLES.includes(userRole);
  const todayIdx = useMemo(() => getTodayIndex(), []);

  // ─── State ──────────────────────────────────────────────────────────────────
  const [refDate, setRefDate] = useState(new Date());
  const weekInfo = useMemo(() => getWeekDetails(refDate), [refDate]);

  const [projectsData, setProjectsData] = useState({ projects: [], clients: [] });
  const [tasks, setTasks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [pendingRows, setPendingRows] = useState([]); // transient rows added via search, not yet favorited
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [weekStatus, setWeekStatus] = useState({
    closed: false, approved: false, can_modify: true
  });
  const [dailyStatus, setDailyStatus] = useState({
    daily_status: {}, can_close_days: false
  });

  // Inline Popup State
  const [showInlinePopup, setShowInlinePopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [popupTasks, setPopupTasks] = useState([]);
  const [popupDay, setPopupDay] = useState(null);
  const [popupSelection, setPopupSelection] = useState(null); // The row being edited

  // Joyride
  const [runTour, setRunTour] = useState(false);
  const [tourSteps] = useState([
    { target: 'body', placement: 'center', title: 'Welcome to Clockwise!', content: 'This guided tour will show you how to manage your timesheets effectively.' },
    { target: '.tour-week-nav', content: 'Use these arrows to navigate between weeks.' },
    { target: '.tour-search', content: 'Search for Projects or Clients here to add them.' },
    { target: '.grid-container', content: 'Your tasks appear here. Click a cell to add hours.' },
    { target: '.tour-hand-in', content: 'Once complete, hand in your week for approval.' },
  ]);

  // ─── Data Loading ───────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requestedEmployeeId = selectedEmployee
        ? (selectedEmployee.employeeId || selectedEmployee.id)
        : null;
      const [tasksRes, statusRes, dailyRes, favsRes] = await Promise.all([
        getClockwiseTasks(weekInfo.year, weekInfo.week, requestedEmployeeId).catch(err => { console.error('Tasks error:', err); return []; }),
        getWeekStatus(weekInfo.year, weekInfo.week, requestedEmployeeId).catch(err => { console.error('Week status error:', err); return { closed: true, approved: false, can_modify: false }; }),
        getDailyStatus(weekInfo.year, weekInfo.week, requestedEmployeeId).catch(err => { console.error('Daily status error:', err); return { daily_status: {}, can_close_days: false, week_closed: true }; }),
        getFavorites().catch(err => { console.error('Favorites error:', err); return []; }),
      ]);

      setTasks(tasksRes.clockwise || tasksRes || []);
      setWeekStatus(statusRes || tasksRes.status || { closed: false, approved: false, can_modify: true });
      setDailyStatus(dailyRes || { daily_status: {}, can_close_days: false });
      setFavorites(favsRes || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [weekInfo, selectedEmployee, currentUser]);

  // Load local project data once on mount (module-level cache means this is instant after first render)
  useEffect(() => {
    getLocalProjectsData()
      .then(data => setProjectsData(data || { projects: [], clients: [] }))
      .catch(err => console.error('Projects data error:', err));
  }, []);

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      getAllEmployees().then(res => {
        const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : res?.users || [];
        setEmployees(raw.map(e => ({ ...e, name: e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim() })));
      });
    }
  }, [fetchData, isAdmin]);

  // Handle Tutorial
  useEffect(() => {
    const seen = localStorage.getItem(`tour_seen_${currentUser?.id}`);
    if (!seen && !loading && currentUser?.id) {
      setRunTour(true);
    }
  }, [loading, currentUser]);

  const handleJoyrideCallback = (data) => {
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(data.status)) {
      setRunTour(false);
      localStorage.setItem(`tour_seen_${currentUser?.id}`, 'true');
    }
  };

  // ─── Grid Logic ─────────────────────────────────────────────────────────────
  const groupedTasks = useMemo(() => {
    const map = {};
    const flatTasks = Array.isArray(tasks) ? tasks : tasks.clockwise || [];

    flatTasks.forEach(task => {
      const p1 = task.pathLevel1 || '';
      const p2 = task.pathLevel2 || '';
      const p3 = task.pathLevel3 || '';
      const key = `${p1}-${p2}-${p3}`;

      if (!map[key]) {
        map[key] = {
          key, pathLevel1: p1, pathLevel2: p2, pathLevel3: p3,
          label: p3 || p2 || p1 || 'Task',
          hours: Array(7).fill(0),
          taskIds: Array(7).fill([])
        };
      }
      if (task.day !== undefined) {
        map[key].hours[task.day] += parseFloat(task.hours || 0);
        map[key].taskIds[task.day] = [...(map[key].taskIds[task.day] || []), task.id];
      }
    });

    // Include favorites as empty rows if not already in tasks
    favorites.forEach(fav => {
      try {
        const payload = JSON.parse(fav.reference_id || '{}');
        const p1 = payload.pathLevel1 || '';
        const p2 = payload.pathLevel2 || '';
        const p3 = payload.pathLevel3 || '';
        const key = `${p1}-${p2}-${p3}`;

        if (!map[key]) {
          map[key] = {
            key, pathLevel1: p1, pathLevel2: p2, pathLevel3: p3,
            label: p3 || p2 || p1 || 'Favorite',
            hours: Array(7).fill(0),
            taskIds: Array(7).fill([])
          };
        }
      } catch (err) {
        // Fallback for legacy
      }
    });

    // Include transient rows added from search (not yet favorited)
    pendingRows.forEach(row => {
      const key = `${row.pathLevel1}-${row.pathLevel2}-${row.pathLevel3}`;
      if (!map[key]) {
        map[key] = { ...row, hours: Array(7).fill(0), taskIds: Array(7).fill([]) };
      }
    });

    return Object.values(map);
  }, [tasks, favorites, pendingRows]);

  const weekTotal = useMemo(() => {
    const flat = Array.isArray(tasks) ? tasks : tasks.clockwise || [];
    return flat.reduce((s, t) => s + parseFloat(t.hours || 0), 0);
  }, [tasks]);

  const dayTotals = useMemo(() => {
    const totals = Array(7).fill(0);
    const flat = Array.isArray(tasks) ? tasks : tasks.clockwise || [];
    flat.forEach(t => { if (t.day !== undefined) totals[t.day] += parseFloat(t.hours || 0); });
    return totals;
  }, [tasks]);

  const handleSearchAdd = (proj, client, svc, legacyMarabe, legacyExt, dept, leaf) => {
    // Only add a transient row to the grid — user must explicitly click ❤ to favorite it
    const p1 = proj?.name || '';
    const p2 = client?.name || dept?.name || dept?.department || '';
    const p3 = svc?.name || svc?.service || '';
    const key = `${p1}-${p2}-${p3}`;
    const label = p3 || p2 || p1 || 'Task';

    setPendingRows(prev => {
      const exists = prev.some(r => r.key === key);
      if (exists) return prev;
      return [...prev, { key, pathLevel1: p1, pathLevel2: p2, pathLevel3: p3, label }];
    });
  };

  const handleCellClick = (e, row, dayIdx) => {
    const isClosed = dailyStatus.daily_status?.[dayIdx]?.is_closed;
    const locked = weekStatus.closed || (isClosed && !isAdmin);

    // Position popup
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPosition({ x: Math.min(rect.left, window.innerWidth - 460), y: rect.bottom + 8 });
    setPopupDay(dayIdx);
    setPopupSelection(row);

    const flat = Array.isArray(tasks) ? tasks : tasks.clockwise || [];
    const cellTasks = flat.filter(t =>
      (t.pathLevel1 || '') === (row.pathLevel1 || '') &&
      (t.pathLevel2 || '') === (row.pathLevel2 || '') &&
      (t.pathLevel3 || '') === (row.pathLevel3 || '') &&
      t.day === dayIdx
    );

    setPopupTasks(cellTasks.length > 0 ? cellTasks : [{ hours: 0, description: '' }]);
    setShowInlinePopup(true);
  };

  const handlePopupChange = (idx, field, val) => {
    const next = [...popupTasks];
    next[idx] = { ...next[idx], [field]: val };
    setPopupTasks(next);
  };

  const savePopup = async () => {
    const dayInfo = popupDay !== null ? dailyStatus.daily_status?.[popupDay] : null;
    const isLocked = weekStatus.closed || ((dayInfo?.is_closed ?? false) && !isAdmin);
    if (isLocked) {
      return;
    }

    setIsSaving(true);
    try {
      const targetEmployeeId = getTargetEmployeeId();
      for (const t of popupTasks) {
        const h = parseFloat(t.hours || 0);
        const payload = {
          day: popupDay, week: weekInfo.week, year: weekInfo.year,
          hours: h, description: t.description || '',
          employeeId: targetEmployeeId,
          pathLevel1: popupSelection.pathLevel1,
          pathLevel2: popupSelection.pathLevel2,
          pathLevel3: popupSelection.pathLevel3,
        };

        if (t.id) {
          if (h === 0) await deleteTask(t.id);
          else await updateTask(t.id, payload);
        } else if (h > 0) {
          await createTasks([payload]);
        }
      }
      await fetchData();
      setShowInlinePopup(false);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleFav = async (row) => {
    const favPayload = JSON.stringify({ pathLevel1: row.pathLevel1, pathLevel2: row.pathLevel2, pathLevel3: row.pathLevel3 });
    const isFav = favorites.some(f => f.reference_id === favPayload);
    if (isFav) await removeFavorite("hierarchy", favPayload);
    else await addFavorite("hierarchy", favPayload);
    fetchData();
  };

  const getTargetEmployeeId = useCallback(() => {
    return selectedEmployee?.employeeId || selectedEmployee?.id || currentUser?.employeeId || null;
  }, [selectedEmployee, currentUser]);

  const handleCloseDays = useCallback(async () => {
    try {
      setIsSaving(true);
      const targetEmployeeId = getTargetEmployeeId();
      const daysToClose = Array.from({ length: 7 }, (_, dayIdx) => dayIdx).filter((dayIdx) => {
        const dayInfo = dailyStatus.daily_status?.[dayIdx];
        return !dayInfo?.is_closed;
      });

      if (daysToClose.length === 0) {
        return;
      }

      await closeMultipleDays(weekInfo.year, weekInfo.week, daysToClose, targetEmployeeId);
      await fetchData();
    } catch (err) {
      console.error('Close days error:', err);
    } finally {
      setIsSaving(false);
    }
  }, [dailyStatus.daily_status, dayTotals, fetchData, getTargetEmployeeId, weekInfo]);

  const handleHandInWeek = useCallback(async () => {
    try {
      setIsSaving(true);
      const targetEmployeeId = getTargetEmployeeId();
      await handInWeek(weekInfo.year, weekInfo.week, targetEmployeeId);
      await fetchData();
    } catch (err) {
      console.error('Hand in error:', err);
    } finally {
      setIsSaving(false);
    }
  }, [fetchData, getTargetEmployeeId, weekInfo]);

  // ─── Render Helpers ────────────────────────────────────────────────────────
  const renderCell = (row, dayIdx) => {
    const h = row.hours[dayIdx];
    const isToday = dayIdx === todayIdx && (weekInfo.week === getWeek(new Date(), { weekStartsOn: 1 }));
    const isClosed = dailyStatus.daily_status?.[dayIdx]?.is_closed;
    const locked = weekStatus.closed || (isClosed && !isAdmin);

    return (
      <Box
        onClick={(e) => handleCellClick(e, row, dayIdx)}
        sx={{
          height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: isToday ? TODAY_BG : 'transparent',
          borderLeft: `1px solid ${BORDER_CLR}`,
          cursor: locked ? 'not-allowed' : 'pointer',
          transition: 'all 0.1s',
          '&:hover': locked ? {} : { bgcolor: alpha(theme.palette.primary.main, 0.05), boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}` },
          fontSize: 14, fontWeight: h > 0 ? 700 : 400, color: h > 0 ? '#1e293b' : '#cbd5e1',
        }}
      >
        {h > 0 ? h : ''}
      </Box>
    );
  };

  const popupDayInfo = popupDay !== null ? dailyStatus.daily_status?.[popupDay] : null;
  const popupLocked = weekStatus.closed || ((popupDayInfo?.is_closed ?? false) && !isAdmin);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
      {/* <Joyride
        steps={tourSteps} run={runTour} continuous showSkipButton showProgress
        callback={handleJoyrideCallback} tooltipComponent={CustomTooltip}
        styles={{ options: { zIndex: 10000 } }}
      /> */}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.02em' }}>
            {format(refDate, 'MMMM yyyy')}
          </Typography>
          <Box className="tour-week-nav" sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton onClick={() => setRefDate(shiftWeek(refDate, -1))} size="small" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setRefDate(shiftWeek(refDate, 1))} size="small" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ px: 2, py: 0.75, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600 }}>
            Week {weekInfo.week} <span style={{ opacity: 0.5, margin: '0 4px' }}>|</span> {weekInfo.start} – {weekInfo.end}
          </Box>
          <Button variant="outlined" size="small" onClick={() => setRefDate(new Date())} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#1e293b' }}>
            Today
          </Button>
          {isAdmin && (
            <Select
              size="small" value={selectedEmployee?.employeeId || selectedEmployee?.id || ''}
              onChange={(e) => setSelectedEmployee(employees.find(em => (em.employeeId || em.id) === e.target.value) || null)}
              displayEmpty sx={{ borderRadius: 2, minWidth: 160, bgcolor: '#fff' }}
            >
              <MenuItem value="">My Timesheet</MenuItem>
              {employees.map(em => <MenuItem key={em.id || em.employeeId} value={em.id || em.employeeId}>{em.name}</MenuItem>)}
            </Select>
          )}
          <IconButton size="small" onClick={fetchData} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}><Refresh fontSize="small" /></IconButton>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} mb={3} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b', mr: 1 }}>STATUS:</Typography>
        {DAY_INITIALS.map((init, i) => {
          const closed = dailyStatus.daily_status?.[i]?.is_closed;
          const hasHrs = dayTotals[i] > 0;
          return (
            <Box key={i} sx={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 800, border: closed ? 'none' : '1px solid #cbd5e1',
              bgcolor: closed ? '#16a34a' : hasHrs ? '#f97316' : 'transparent',
              color: closed || hasHrs ? '#fff' : '#64748b',
              ...(i === todayIdx ? { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` } : {})
            }}>
              {init}
            </Box>
          );
        })}
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<CheckCircle />}
          sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}
          onClick={handleCloseDays}
          disabled={isSaving || (weekStatus.closed && !isAdmin)}
        >
          Close Days
        </Button>
        <Button
          className="tour-hand-in"
          variant="contained"
          size="small"
          startIcon={<LockOpen />}
          sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}
          onClick={handleHandInWeek}
          disabled={isSaving || (weekStatus.closed && !isAdmin)}
        >
          Hand In
        </Button>
        {weekStatus.closed && isAdmin && !weekStatus.approved && (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" color="success" onClick={() => approveWeek(weekInfo.year, weekInfo.week, getTargetEmployeeId()).then(fetchData)} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Approve
            </Button>
            <Button variant="outlined" size="small" color="error" onClick={() => reopenWeek(weekInfo.year, weekInfo.week, getTargetEmployeeId()).then(fetchData)} sx={{ borderRadius: 2, textTransform: 'none' }}>
              Reopen
            </Button>
          </Stack>
        )}
        {/* HR Bulk Actions — visible only for admin when no specific employee is selected */}
        {isAdmin && !selectedEmployee && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Close all open days (with hours) for every employee this week" arrow>
              <Button
                variant="outlined" size="small"
                startIcon={<CheckCircle />}
                sx={{ borderRadius: 2, textTransform: 'none', px: 2, color: '#16a34a', borderColor: '#16a34a' }}
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    await Promise.all(
                      Array.from({ length: 7 }, (_, i) => i).map(day =>
                        closeDayAllUsers(weekInfo.year, weekInfo.week, day).catch(() => { })
                      )
                    );
                    await fetchData();
                  } finally { setIsSaving(false); }
                }}
                disabled={isSaving}
              >
                Close All
              </Button>
            </Tooltip>
            <Tooltip title="Hand in this week for all employees" arrow>
              <Button
                variant="outlined" size="small"
                startIcon={<LockOpen />}
                sx={{ borderRadius: 2, textTransform: 'none', px: 2, color: '#2563eb', borderColor: '#2563eb' }}
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    await handInWeekAllUsers(weekInfo.year, weekInfo.week);
                    await fetchData();
                  } finally { setIsSaving(false); }
                }}
                disabled={isSaving}
              >
                Hand In All
              </Button>
            </Tooltip>
          </>
        )}
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Tooltip title="This feature is currently under development" arrow placement="top">
          <span>
            <Button
              variant="outlined" size="small" startIcon={<ContentCopy />}
              sx={{ borderRadius: 2, textTransform: 'none', px: 2, color: '#334155', borderColor: '#e2e8f0' }}
              disabled
            >
              Export Excel
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <Box className="tour-search">
              <SearchDropdown
                projects={projectsData.projects}
                clients={projectsData.clients}
                onSelect={handleSearchAdd}
                loading={loading && projectsData.projects.length === 0}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${BORDER_CLR}`, borderRadius: 4, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box className="grid-container" sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, bgcolor: '#f1f5f9', borderBottom: `1px solid ${BORDER_CLR}` }}>
          <Box sx={{ p: 2, fontSize: 13, fontWeight: 700, color: '#64748b' }}>TASK / PROJECT</Box>
          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d, i) => (
            <Box key={d} sx={{ p: 1.5, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#64748b', borderLeft: `1px solid ${BORDER_CLR}`, bgcolor: i === todayIdx ? TODAY_BG : 'transparent' }}>
              {d}
            </Box>
          ))}
          <Box sx={{ p: 2, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#64748b', borderLeft: `1px solid ${BORDER_CLR}` }}>TOTAL</Box>
        </Box>

        <Box sx={{ minHeight: 200 }}>
          {groupedTasks.map((row, rIdx) => {
            const favPayload = JSON.stringify({ pathLevel1: row.pathLevel1, pathLevel2: row.pathLevel2, pathLevel3: row.pathLevel3 });
            const isFav = favorites.some(f => f.reference_id === favPayload);
            return (
              <Box key={row.key} sx={{
                display: 'grid', gridTemplateColumns: GRID_COLUMNS, borderBottom: `1px solid ${BORDER_CLR}`,
                '&:hover': { bgcolor: '#f8fafc' }, bgcolor: rIdx % 2 ? '#fafafa' : '#fff'
              }}>
                <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderRight: `1px solid ${BORDER_CLR}`, overflow: 'hidden' }}>
                  <IconButton size="small" onClick={() => toggleFav(row)} sx={{ color: isFav ? '#ef4444' : '#cbd5e1', flexShrink: 0 }}>
                    <Favorite sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                    {/* Line 1: Project code */}
                    {row.pathLevel1 && (
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#1e293b', display: 'block', lineHeight: 1.2, letterSpacing: '0.02em' }}>
                        {row.pathLevel1}
                      </Typography>
                    )}
                    {/* Line 2: Task name (p3) */}
                    {row.pathLevel3 && (
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569', display: 'block', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.pathLevel3}
                      </Typography>
                    )}
                    {/* Line 3: Client name (p2) — shown if different from p1 */}
                    {row.pathLevel2 && row.pathLevel2 !== row.pathLevel1 && (
                      <Typography variant="caption" sx={{ fontWeight: 400, color: '#94a3b8', display: 'block', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                        {row.pathLevel2}
                      </Typography>
                    )}
                    {/* Fallback: show label if all path levels are empty */}
                    {!row.pathLevel1 && !row.pathLevel2 && !row.pathLevel3 && (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        {row.label}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {row.hours.map((_, dIdx) => (
                  <Box key={dIdx} sx={{ borderLeft: dIdx === 0 ? 'none' : `1px solid ${BORDER_CLR}` }}>
                    {renderCell(row, dIdx)}
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#1e293b', borderLeft: `1px solid ${BORDER_CLR}` }}>
                  {row.hours.reduce((s, h) => s + h, 0) || ''}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, bgcolor: '#f1f5f9', position: 'sticky', bottom: 0, zIndex: 10 }}>
          <Box sx={{ p: 2, fontSize: 13, fontWeight: 800, color: '#334155' }}>WEEK TOTAL</Box>
          {dayTotals.map((tot, i) => (
            <Box key={i} sx={{ p: 2, textAlign: 'center', fontWeight: 800, color: tot === 0 ? '#cbd5e1' : '#1e293b', borderLeft: `1px solid ${BORDER_CLR}`, bgcolor: i === todayIdx ? TODAY_BG : 'transparent' }}>
              {tot || '0'}
            </Box>
          ))}
          <Box sx={{ p: 2, textAlign: 'center', fontWeight: 900, color: theme.palette.primary.main, borderLeft: `1px solid ${BORDER_CLR}` }}>
            {weekTotal}
          </Box>
        </Box>
      </Paper>

      {showInlinePopup && (
        <>
          <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300 }} onClick={() => setShowInlinePopup(false)} />
          <Paper
            elevation={24}
            sx={{
              position: 'fixed', left: popupPosition.x, top: popupPosition.y, zIndex: 1301,
              width: 450, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0',
              animation: 'fadeInUp 0.2s ease-out'
            }}
          >
            <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight={700}>Uren & Opmerking</Typography>
              <IconButton size="small" onClick={() => setShowInlinePopup(false)}><Close fontSize="small" /></IconButton>
            </Box>
            {popupLocked && (
              <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
                This day/week is closed. Editing is disabled.
              </Alert>
            )}
            <Box sx={{ p: 2, maxHeight: 300, overflowY: 'auto' }}>
              {popupTasks.map((t, idx) => (
                <Stack key={idx} direction="row" spacing={1} sx={{ mb: idx === popupTasks.length - 1 ? 0 : 2 }}>
                  <TextField
                    size="small" type="number" sx={{ width: 80 }} placeholder="0.00"
                    value={t.hours} onChange={(e) => handlePopupChange(idx, 'hours', e.target.value)}
                    disabled={popupLocked}
                  />
                  <TextField
                    size="small" fullWidth placeholder="Description..."
                    value={t.description} onChange={(e) => handlePopupChange(idx, 'description', e.target.value)}
                    disabled={popupLocked}
                  />
                  <IconButton size="small" onClick={() => {
                    if (popupLocked) return;
                    if (t.id) {
                      deleteTask(t.id).then(() => fetchData());
                    }
                    const next = [...popupTasks];
                    next.splice(idx, 1);
                    setPopupTasks(next.length ? next : [{ hours: 0, description: '' }]);
                  }} color="error" disabled={popupLocked}><DeleteOutline fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Button
                startIcon={<Add />} variant="text" size="small" fullWidth sx={{ mt: 1, textTransform: 'none' }}
                onClick={() => setPopupTasks([...popupTasks, { hours: 0, description: '' }])}
                disabled={popupLocked}
              >
                Add entry for this day
              </Button>
            </Box>
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained" size="small" onClick={savePopup} disabled={isSaving || popupLocked}
                sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
